import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/tracking';

export const Hero = () => {
  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 md:pt-24 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[#ff00c7]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#eaddcf]/50 rounded-full blur-3xl" />
      </div>

      <div className="container-main relative z-10 py-20 md:py-32">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center"
        >
          {/* Left content */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div variants={itemVariants}>
              <span className="inline-block text-xs md:text-sm font-medium uppercase tracking-widest text-[#242328]/60 mb-4">
                {t.hero.badge}
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              data-testid="hero-headline"
              className="font-['Syne'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#242328] leading-[0.95] tracking-tight"
            >
              {t.hero.headline}{' '}
              <span className="text-[#ff00c7]">{t.hero.headlineAccent}</span>.
            </motion.h1>

            <motion.p
              variants={itemVariants}
              data-testid="hero-subheadline"
              className="text-base md:text-lg lg:text-xl text-[#242328]/70 leading-relaxed max-w-2xl"
            >
              {t.hero.subheadline}
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/form"
                data-testid="hero-cta"
                onClick={() => trackEvent('cta_click')}
                className="btn-primary group"
              >
                {t.hero.cta}
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <button
                onClick={() => document.querySelector('#servicos')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-secondary-cta"
                className="btn-secondary"
              >
                {t.hero.secondaryCta}
              </button>
            </motion.div>
          </div>

          {/* Right visual */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden hidden md:block">
              <img
                src="https://images.unsplash.com/photo-1770119618410-5c8a7cc622c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHBpbmslMjBsaW5lcyUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzczNzAwOTE2fDA&ixlib=rb-4.1.0&q=85"
                alt="Abstract creative design"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#242328]/20 to-transparent" />
            </div>
            
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="md:absolute md:-bottom-6 md:-left-6 bg-[#242328] text-[#f8f6e2] px-6 py-4 rounded-2xl shadow-xl max-w-[280px] md:max-w-[200px] mx-auto md:mx-0 text-center md:text-left"
            >
              <p className="text-sm text-[#f8f6e2]/90 leading-tight">{t.hero.trustedBy}</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-[#242328]/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-1.5 bg-[#242328]/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};
